import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  FileText, Download, Send, RotateCcw, Check, X, Loader2,
  Mail, Plus, Clock, AlertCircle, Trash2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import CreateContractQuoteModal from './CreateContractQuoteModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pydantic 422 -> detail = [{type, loc, msg, ...}]. Extraction safe.
const extractErr = (e, fb = 'Échec') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fb;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => (typeof x === 'string' ? x : (x?.msg || x?.message || JSON.stringify(x)))).join(' · ');
  if (typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return String(d);
};

const STATUS_BADGE = {
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  sent: { label: 'Envoyé', cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  accepted: { label: 'Accepté', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  refused: { label: 'Refusé', cls: 'bg-red-50 text-red-700 border-red-300' },
  expired: { label: 'Expiré', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return String(d).slice(0, 10); }
};

const ContractQuoteHistoryTab = ({ contract }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editQuote, setEditQuote] = useState(null);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [refuseTarget, setRefuseTarget] = useState(null);
  const [refuseReason, setRefuseReason] = useState('');
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [acceptedBy, setAcceptedBy] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // PM Spec 30/04/2026 (Q2=B) — par défaut on cache les brouillons/expirés/refusés
  const [showArchive, setShowArchive] = useState(false);

  const fetchQuotes = useCallback(async () => {
    if (!contract?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contracts/${contract.id}/quotes`);
      setQuotes(r.data || []);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de charger les devis', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [contract?.id]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const downloadPdf = async (q, version = null) => {
    try {
      const path = version && version !== q.version
        ? `${API}/contracts/${contract.id}/quotes/${q.id}/pdf/v/${version}`
        : `${API}/contracts/${contract.id}/quotes/${q.id}/pdf`;
      const r = await axios.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis_${q.quoteNumber}_V${version || q.version}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Erreur', description: 'PDF indisponible', variant: 'destructive' });
    }
  };

  const handleSend = async (q) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/contracts/${contract.id}/quotes/${q.id}/send`);
      toast({ title: 'Succès', description: 'Devis envoyé au client' });
      await fetchQuotes();
    } catch (e) {
      toast({ title: 'Erreur', description: extractErr(e, 'Échec envoi'), variant: 'destructive' });
    } finally { setActionLoading(false); }
  };

  const handleResend = async (q) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/contracts/${contract.id}/quotes/${q.id}/resend`);
      toast({ title: 'Succès', description: 'Devis renvoyé au client' });
      await fetchQuotes();
    } catch (e) {
      toast({ title: 'Erreur', description: extractErr(e), variant: 'destructive' });
    } finally { setActionLoading(false); }
  };

  const submitReopen = async () => {
    if (!reopenReason || reopenReason.trim().length < 3) {
      toast({ title: 'Validation', description: 'Raison obligatoire (min. 3 caractères)', variant: 'destructive' });
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/contracts/${contract.id}/quotes/${reopenTarget.id}/reopen`,
        { reason: reopenReason.trim() });
      toast({ title: 'Succès', description: 'Devis rouvert pour révision' });
      setReopenTarget(null); setReopenReason('');
      await fetchQuotes();
    } catch (e) {
      toast({ title: 'Erreur', description: extractErr(e), variant: 'destructive' });
    } finally { setActionLoading(false); }
  };

  const submitAccept = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/contracts/${contract.id}/quotes/${acceptTarget.id}/accept`,
        { acceptedBy: acceptedBy.trim() || null });
      toast({ title: 'Succès', description: 'Devis marqué accepté' });
      setAcceptTarget(null); setAcceptedBy('');
      await fetchQuotes();
    } catch (e) {
      toast({ title: 'Erreur', description: extractErr(e), variant: 'destructive' });
    } finally { setActionLoading(false); }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`Supprimer définitivement le devis ${q.quoteNumber} ?\nCette action est irréversible.`)) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/contracts/${contract.id}/quotes/${q.id}`);
      toast({ title: 'Succès', description: `Devis ${q.quoteNumber} supprimé` });
      await fetchQuotes();
    } catch (e) {
      toast({ title: 'Erreur', description: extractErr(e, 'Échec suppression'), variant: 'destructive' });
    } finally { setActionLoading(false); }
  };

  const handleCleanupAllDrafts = async () => {
    const drafts = (quotes || []).filter(q => q.status === 'draft' && (q.version || 1) === 1);
    if (drafts.length === 0) {
      toast({ title: 'Aucun brouillon', description: 'Rien à nettoyer.' });
      return;
    }
    if (!window.confirm(`Supprimer définitivement ${drafts.length} brouillon${drafts.length > 1 ? 's' : ''} ?\nCette action est irréversible.`)) return;
    setActionLoading(true);
    try {
      let deleted = 0; const errors = [];
      for (const q of drafts) {
        try {
          await axios.delete(`${API}/contracts/${contract.id}/quotes/${q.id}`);
          deleted++;
        } catch (e) {
          errors.push(`${q.quoteNumber}`);
        }
      }
      toast({
        title: 'Nettoyage terminé',
        description: `${deleted}/${drafts.length} brouillons supprimés` + (errors.length ? ` · ${errors.length} échec(s)` : ''),
      });
      await fetchQuotes();
    } finally { setActionLoading(false); }
  };


  const submitRefuse = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/contracts/${contract.id}/quotes/${refuseTarget.id}/refuse`,
        { reason: refuseReason.trim() || null });
      toast({ title: 'Succès', description: 'Devis marqué refusé' });
      setRefuseTarget(null); setRefuseReason('');
      await fetchQuotes();
    } catch (e) {
      toast({ title: 'Erreur', description: extractErr(e), variant: 'destructive' });
    } finally { setActionLoading(false); }
  };

  const canCreateQuote = contract && ['draft', 'active', 'suspended'].includes(contract.status);

  // PM Spec 30/04/2026 (Q2=B) — Sépare les devis "actifs" des "archives" (drafts/expired/refused).
  // Tri descendant par updatedAt pour que le plus récent ressorte.
  const sortedQuotes = React.useMemo(() => {
    return [...(quotes || [])].sort((a, b) => {
      const da = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const db = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return db - da;
    });
  }, [quotes]);
  const ACTIVE_STATUSES = ['sent', 'accepted'];
  const activeQuotes = sortedQuotes.filter(q => ACTIVE_STATUSES.includes(q.status));
  const archivedQuotes = sortedQuotes.filter(q => !ACTIVE_STATUSES.includes(q.status));
  const displayedQuotes = showArchive ? sortedQuotes : activeQuotes;

  return (
    <div className="space-y-4" data-testid="contract-quote-history-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold">Propositions commerciales</h3>
          <p className="text-sm text-gray-500">
            {showArchive
              ? `Tous les devis (${sortedQuotes.length})`
              : `${activeQuotes.length} devis actif${activeQuotes.length > 1 ? 's' : ''}${archivedQuotes.length > 0 ? ` · ${archivedQuotes.length} archivé${archivedQuotes.length > 1 ? 's' : ''}` : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showArchive && archivedQuotes.filter(q => q.status === 'draft').length >= 2 && (
            <Button variant="ghost" size="sm" onClick={handleCleanupAllDrafts}
                    disabled={actionLoading}
                    data-testid="cleanup-drafts-btn"
                    className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Nettoyer les brouillons ({archivedQuotes.filter(q => q.status === 'draft').length})
            </Button>
          )}
          {archivedQuotes.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowArchive(s => !s)}
                    data-testid="toggle-archive-btn"
                    className="text-xs">
              {showArchive
                ? 'Masquer les archivés'
                : `Voir les ${archivedQuotes.length} archivé${archivedQuotes.length > 1 ? 's' : ''}`}
            </Button>
          )}
          {canCreateQuote && (
            <Button onClick={() => { setEditQuote(null); setShowCreateModal(true); }}
                    data-testid="create-contract-quote-btn"
                    className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> Nouveau devis
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      ) : displayedQuotes.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{showArchive || sortedQuotes.length === 0 ? 'Aucun devis pour ce contrat.' : 'Aucun devis actif. Cliquez sur "Voir les archivés" pour afficher les brouillons et devis terminés.'}</p>
          {canCreateQuote && sortedQuotes.length === 0 && <p className="text-sm mt-1">Créez le premier devis pour ce client.</p>}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {displayedQuotes.map(q => {
            const status = STATUS_BADGE[q.status] || STATUS_BADGE.draft;
            const isExpired = q.status === 'expired';
            return (
              <Card key={q.id} className={`border ${isExpired ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'}`}
                    data-testid={`quote-row-${q.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">{q.quoteNumber}</span>
                          <Badge variant="outline" className="text-xs">V{q.version}</Badge>
                          <Badge variant="outline" className={`text-xs ${status.cls}`}>{status.label}</Badge>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {(q.totalTTC || 0).toLocaleString('fr-FR')} F TTC · valable jusqu'au {formatDate(q.validUntil)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(q)}
                              title="Télécharger PDF" data-testid={`download-pdf-${q.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {q.status === 'draft' && (
                        <>
                          <Button size="sm" variant="outline"
                                  onClick={() => { setEditQuote(q); setShowCreateModal(true); }}
                                  data-testid={`edit-${q.id}`}>Modifier</Button>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                                  disabled={actionLoading} onClick={() => handleSend(q)}
                                  data-testid={`send-${q.id}`}>
                            <Send className="w-3.5 h-3.5 mr-1" /> Envoyer
                          </Button>
                          {(q.version || 1) === 1 && (
                            <Button size="sm" variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    disabled={actionLoading} onClick={() => handleDelete(q)}
                                    data-testid={`delete-${q.id}`}
                                    title="Supprimer ce brouillon">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </>
                      )}
                      {q.status === 'sent' && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleResend(q)}
                                  disabled={actionLoading} title="Renvoyer email">
                            <Mail className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300"
                                  onClick={() => setAcceptTarget(q)} data-testid={`accept-${q.id}`}>
                            <Check className="w-3.5 h-3.5 mr-1" /> Accepté
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-700 border-red-300"
                                  onClick={() => setRefuseTarget(q)} data-testid={`refuse-${q.id}`}>
                            <X className="w-3.5 h-3.5 mr-1" /> Refusé
                          </Button>
                        </>
                      )}
                      {(['sent', 'refused', 'expired'].includes(q.status)) && (
                        <Button size="sm" variant="ghost" onClick={() => setReopenTarget(q)}
                                data-testid={`reopen-${q.id}`}
                                title="Réviser ce devis (créer une nouvelle version)">
                          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Réviser
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Versions archivées */}
                  {(q.pdfVersions || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Versions précédentes
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {q.pdfVersions.map(pv => (
                          <Button key={pv.version} size="sm" variant="outline"
                                  className="h-6 text-xs"
                                  onClick={() => downloadPdf(q, pv.version)}>
                            <Download className="w-3 h-3 mr-1" /> V{pv.version} · {(pv.totalTTC || 0).toLocaleString('fr-FR')} F
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {q.reopenReason && q.version > 1 && (
                    <div className="mt-2 text-xs text-gray-500 italic flex items-start gap-1">
                      <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      Révision : {q.reopenReason}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal création/édition */}
      <CreateContractQuoteModal
        open={showCreateModal}
        onOpenChange={(o) => { setShowCreateModal(o); if (!o) setEditQuote(null); }}
        contract={contract}
        existingQuote={editQuote}
        onSuccess={fetchQuotes}
      />

      {/* Reopen */}
      <Dialog open={!!reopenTarget} onOpenChange={(o) => !o && setReopenTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Réviser le devis {reopenTarget?.quoteNumber}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Raison de la révision <span className="text-red-500">*</span></Label>
            <Textarea value={reopenReason} onChange={e => setReopenReason(e.target.value)}
                      placeholder="Ex : Renégociation tarifaire client / Erreur de calcul / Changement de périmètre"
                      rows={3} data-testid="reopen-reason-input" />
            <p className="text-xs text-gray-500">Une nouvelle version V{(reopenTarget?.version || 1) + 1} sera créée et les valeurs précédentes seront archivées.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenTarget(null)}>Annuler</Button>
            <Button onClick={submitReopen} disabled={actionLoading} data-testid="confirm-reopen-btn">
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmer la révision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept */}
      <Dialog open={!!acceptTarget} onOpenChange={(o) => !o && setAcceptTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marquer accepté — {acceptTarget?.quoteNumber}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Nom du contact côté client (optionnel)</Label>
            <Input value={acceptedBy} onChange={e => setAcceptedBy(e.target.value)}
                   placeholder="Ex : Marina GBA" />
            <p className="text-xs text-gray-500">L'acceptation du devis ne change pas automatiquement le statut du contrat.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptTarget(null)}>Annuler</Button>
            <Button onClick={submitAccept} disabled={actionLoading}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-accept-btn">
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refuse */}
      <Dialog open={!!refuseTarget} onOpenChange={(o) => !o && setRefuseTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marquer refusé — {refuseTarget?.quoteNumber}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Raison du refus (optionnel)</Label>
            <Textarea value={refuseReason} onChange={e => setRefuseReason(e.target.value)}
                      placeholder="Ex : Tarif trop élevé / Choix d'un autre prestataire" rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefuseTarget(null)}>Annuler</Button>
            <Button onClick={submitRefuse} disabled={actionLoading} variant="destructive"
                    data-testid="confirm-refuse-btn">
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractQuoteHistoryTab;
